// Retarget lost leads — message drafts (client, 2026-11-04).
//
// Admin / Super Admin: see how many clients were closed Lost for each reason,
// and draft the WhatsApp message that will win them back. Nothing is sent from
// this page — sending comes later, through Interakt approved Marketing
// templates. The "Template for Interakt" box is the body to submit there.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, Copy, Megaphone, Save } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { cn } from '@/shared/utils/utils';

interface Reason { reasonKey: string; name: string; active: boolean; excluded: boolean; records: number; mobiles: number }
interface ReasonsResponse { items: Reason[]; totals: { records: number; retargetable: number }; placeholders: string[]; maxMessage: number }
interface Draft {
  id: string; reasonKey: string; reasonName: string; title: string; message: string;
  updatedByName: string; updatedAt: string; template: { body: string; variables: string[] }; hasOptOut: boolean;
}
interface PreviewClient { id: string; phone: string; lostOn: string | null; lostNote: string; values: Record<string, string> }

const PERIODS = [
  { days: 0, label: 'All time' }, { days: 30, label: 'Last 30 days' }, { days: 90, label: 'Last 90 days' }, { days: 180, label: 'Last 6 months' },
];
const FIELD_HINT: Record<string, string> = {
  name: "client's name", company: 'company', product: 'what they enquired for', salesperson: 'their salesperson',
};

/** Named fields → {{1}}, {{2}}… in order of first use (same rule as the server). */
function toTemplate(message: string) {
  const order: string[] = [];
  const body = message.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_m, raw: string) => {
    const k = raw.toLowerCase();
    if (!order.includes(k)) order.push(k);
    return `{{${order.indexOf(k) + 1}}}`;
  });
  return { body, variables: order };
}
const render = (message: string, values: Record<string, string>) =>
  message.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (whole, raw: string) => values[raw.toLowerCase()] || whole);

export function RetargetDraftsPage() {
  const qc = useQueryClient();
  const [days, setDays] = useState(0);
  const [selected, setSelected] = useState<string>('');

  const reasons = useQuery({
    queryKey: ['retarget', 'reasons', days],
    queryFn: async () => (await api.get<ReasonsResponse>(`/api/retarget/reasons${days ? `?days=${days}` : ''}`)).data,
    placeholderData: (prev) => prev,
  });
  const drafts = useQuery({
    queryKey: ['retarget', 'drafts'],
    queryFn: async () => (await api.get<{ items: Draft[] }>('/api/retarget/drafts')).data.items,
  });
  const draftFor = useMemo(() => new Map((drafts.data ?? []).map((d) => [d.reasonKey, d])), [drafts.data]);

  const items = reasons.data?.items ?? [];
  // Open the biggest reason that can be retargeted first.
  useEffect(() => {
    if (selected || !items.length) return;
    const first = [...items].filter((i) => !i.excluded).sort((a, b) => b.records - a.records)[0];
    if (first) setSelected(first.reasonKey);
  }, [items, selected]);
  const current = items.find((i) => i.reasonKey === selected);

  return (
    <div className="space-y-4 pb-16">
      <PageHeader
        icon={Megaphone}
        eyebrow="Sales"
        title="Retarget Lost Leads"
        description={<>Draft the WhatsApp message to win back clients closed <b>Lost</b>, one for each lost reason.
          Nothing is sent from here — the message is ready to submit to Interakt as a Marketing template when sending is switched on.</>}
      />

      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button key={p.days} type="button" onClick={() => setDays(p.days)}
            className={cn('rounded-full border px-3 py-1 text-xs font-medium',
              days === p.days ? 'border-primary bg-primary text-on-primary' : 'border-outline text-on-surface-variant hover:border-primary')}>
            {p.label}
          </button>
        ))}
        {reasons.data && (
          <span className="text-xs text-on-surface-variant">
            {reasons.data.totals.records} lost records · <b className="text-on-surface">{reasons.data.totals.retargetable}</b> mobiles to retarget (Irrelevant left out)
          </span>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <Card>
          <CardHeader title="Lost reasons" />
          <CardBody className="p-2">
            {reasons.isLoading ? <Skeleton className="h-64" /> : (
              <ul className="space-y-1">
                {items.map((r) => {
                  const d = draftFor.get(r.reasonKey);
                  return (
                    <li key={r.reasonKey}>
                      <button type="button" disabled={r.excluded} onClick={() => setSelected(r.reasonKey)}
                        className={cn('flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm',
                          r.excluded ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted',
                          selected === r.reasonKey && 'bg-primary/10 ring-1 ring-primary/30')}>
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{r.name}</span>
                          <span className="text-[11px] text-on-surface-variant">
                            {r.excluded ? 'Left out of retargeting' : `${r.mobiles} mobiles · ${r.records} records`}
                          </span>
                        </span>
                        {!r.excluded && (d
                          ? <Badge className="shrink-0 bg-success/10 text-success-text">Draft saved</Badge>
                          : <Badge className="shrink-0 bg-muted text-on-surface-variant">No draft</Badge>)}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </Card>

        {current && !current.excluded ? (
          <DraftEditor
            key={current.reasonKey}
            reason={current}
            days={days}
            draft={draftFor.get(current.reasonKey)}
            placeholders={reasons.data?.placeholders ?? []}
            maxMessage={reasons.data?.maxMessage ?? 1024}
            onSaved={() => qc.invalidateQueries({ queryKey: ['retarget', 'drafts'] })}
          />
        ) : (
          <Card><CardBody className="p-10 text-center text-sm text-on-surface-variant">Choose a lost reason to draft its message.</CardBody></Card>
        )}
      </div>
    </div>
  );
}

function DraftEditor({ reason, days, draft, placeholders, maxMessage, onSaved }: {
  reason: Reason; days: number; draft?: Draft; placeholders: string[]; maxMessage: number; onSaved: () => void;
}) {
  const preview = useQuery({
    queryKey: ['retarget', 'preview', reason.reasonKey, days],
    queryFn: async () => (await api.get<{ suggested: string; clients: PreviewClient[] }>(`/api/retarget/preview/${reason.reasonKey}${days ? `?days=${days}` : ''}`)).data,
  });
  const [title, setTitle] = useState(draft?.title || '');
  const [message, setMessage] = useState(draft?.message || '');
  const [sample, setSample] = useState(0);
  const box = useRef<HTMLTextAreaElement>(null);

  // A reason with no draft yet starts from the suggested text.
  useEffect(() => {
    if (!draft && !message && preview.data?.suggested) setMessage(preview.data.suggested);
  }, [draft, message, preview.data?.suggested]);

  const save = useMutation({
    mutationFn: async () => (await api.put(`/api/retarget/drafts/${reason.reasonKey}`, { title, message })).data,
    onSuccess: () => { toast.success(`Draft saved for ${reason.name}.`); onSaved(); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not save the draft.')),
  });

  const insert = (field: string) => {
    const el = box.current;
    const token = `{{${field}}}`;
    if (!el) { setMessage((m) => `${m}${token}`); return; }
    const start = el.selectionStart ?? message.length;
    const end = el.selectionEnd ?? message.length;
    setMessage(message.slice(0, start) + token + message.slice(end));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
  };

  const clients = preview.data?.clients ?? [];
  const client = clients[sample];
  const template = toTemplate(message);
  const tooLong = message.length > maxMessage;
  const optOut = /\bSTOP\b/.test(message);
  const unknown = [...new Set([...message.matchAll(/\{\{\s*([a-z_]+)\s*\}\}/gi)].map((m) => m[1].toLowerCase()).filter((p) => !placeholders.includes(p)))];
  const dirty = message !== (draft?.message || '') || title !== (draft?.title || '');

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={`${reason.name} — ${reason.mobiles} mobiles`}
          action={draft ? <span className="text-xs text-on-surface-variant">Saved by {draft.updatedByName || '—'} · {new Date(draft.updatedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span> : null}
        />
        <CardBody className="space-y-3">
          <label className="block text-xs font-semibold text-on-surface-variant">Name for this message (for you, not sent)
            <input className="mt-1 h-9 w-full rounded border border-outline bg-surface px-2 text-sm" maxLength={120}
              placeholder={`e.g. ${reason.name} — November offer`} value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-semibold text-on-surface-variant">Insert:</span>
              {placeholders.map((p) => (
                <button key={p} type="button" onClick={() => insert(p)} title={FIELD_HINT[p] || p}
                  className="rounded border border-outline px-2 py-0.5 font-mono text-[11px] hover:border-primary hover:text-primary">{`{{${p}}}`}</button>
              ))}
            </div>
            <textarea ref={box} rows={7} value={message} onChange={(e) => setMessage(e.target.value)}
              className={cn('w-full rounded border bg-surface px-3 py-2 text-sm', tooLong ? 'border-danger' : 'border-outline')} />
            <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-[11px]">
              <span className={tooLong ? 'font-semibold text-danger-text' : 'text-on-surface-variant'}>{message.length} / {maxMessage} characters</span>
              <span className="flex flex-wrap gap-3">
                {optOut
                  ? <span className="inline-flex items-center gap-1 text-success-text"><CheckCircle2 className="h-3 w-3" /> Has a STOP opt-out</span>
                  : <span className="inline-flex items-center gap-1 text-warning-text"><AlertTriangle className="h-3 w-3" /> Add an opt-out line, e.g. “Reply STOP to stop these messages” — Meta expects it</span>}
                {unknown.length > 0 && <span className="text-danger-text">Unknown: {unknown.map((u) => `{{${u}}}`).join(', ')}</span>}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {preview.data?.suggested && message !== preview.data.suggested && (
              <Button size="sm" variant="ghost" onClick={() => setMessage(preview.data!.suggested)}>Use suggested text</Button>
            )}
            <Button size="sm" disabled={!dirty || !message.trim() || tooLong || unknown.length > 0 || save.isPending} onClick={() => save.mutate()}>
              <Save className="mr-1 h-4 w-4" /> {save.isPending ? 'Saving…' : 'Save draft'}
            </Button>
          </div>
        </CardBody>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader
            title="Preview for a client"
            action={clients.length > 1 ? (
              <select aria-label="Preview client" className="h-8 max-w-[220px] rounded border border-outline bg-surface px-2 text-xs" value={sample} onChange={(e) => setSample(Number(e.target.value))}>
                {clients.map((c, i) => <option key={c.id} value={i}>{c.values.company || c.values.name || 'Client'} {c.phone}</option>)}
              </select>
            ) : null}
          />
          <CardBody>
            {preview.isLoading ? <Skeleton className="h-32" /> : !client ? (
              <p className="text-sm text-on-surface-variant">No clients in this reason{days ? ' for this period' : ''}.</p>
            ) : (
              <>
                <div className="rounded-2xl rounded-tl-sm bg-success/10 px-4 py-3 text-sm whitespace-pre-wrap text-on-surface">{render(message, client.values)}</div>
                <p className="mt-2 text-[11px] text-on-surface-variant">
                  {client.values.company} · {client.phone} · lost {client.lostOn ? new Date(client.lostOn).toLocaleDateString('en-IN') : '—'}
                  {client.lostNote ? ` · note: “${client.lostNote}”` : ''}
                </p>
              </>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader
            title="Template for Interakt"
            action={<Button size="sm" variant="ghost" onClick={() => { void navigator.clipboard?.writeText(template.body); toast.success('Template text copied.'); }}><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>}
          />
          <CardBody className="space-y-2">
            <pre className="whitespace-pre-wrap rounded bg-muted px-3 py-2 font-sans text-sm">{template.body || '—'}</pre>
            {template.variables.length > 0 && (
              <p className="text-[11px] text-on-surface-variant">
                {template.variables.map((v, i) => `{{${i + 1}}} = ${FIELD_HINT[v] || v}`).join(' · ')}
              </p>
            )}
            <p className="text-[11px] text-on-surface-variant">Submit this in Interakt as a <b>Marketing</b> template. Meta approves it before it can be sent.</p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default RetargetDraftsPage;
